import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AgentMergeRequestsService } from 'ag-common-svc/lib/services/agent-merge-requests.service';
import { QueryParam, WhereFilterOperandKeys } from '../dao/CommonFireStoreDao.dao';
import { MergeStatus } from '@ag-common-lib/lib';

@Injectable({ providedIn: 'root' })
export class AgentMergeQueueGuardService {
  private readonly blockedStatuses = [MergeStatus.initial, MergeStatus.queued, MergeStatus.inProgress];
  private readonly blockedByAgentDbId = new Map<string, boolean>();
  private readonly pendingByAgentDbId = new Map<string, Promise<boolean>>();
  private selectedAgentDbId: string | null = null;

  constructor(private agentMergeRequestsService: AgentMergeRequestsService) {}

  public async isAgentMergeBlocked(agentDbId: string | null | undefined): Promise<boolean> {
    if (!agentDbId) {
      return false;
    }

    if (this.blockedByAgentDbId.has(agentDbId)) {
      return this.blockedByAgentDbId.get(agentDbId);
    }

    const pending = this.pendingByAgentDbId.get(agentDbId);
    if (pending) {
      return pending;
    }

    const request = this.loadIsAgentMergeBlocked(agentDbId);
    this.pendingByAgentDbId.set(agentDbId, request);

    void request.then(
      () => this.pendingByAgentDbId.delete(agentDbId),
      () => this.pendingByAgentDbId.delete(agentDbId),
    );

    return request;
  }

  public setSelectedAgentId(agentDbId: string | null | undefined): void {
    const nextAgentDbId = agentDbId ?? null;

    if (this.selectedAgentDbId === nextAgentDbId) {
      return;
    }

    this.selectedAgentDbId = nextAgentDbId;
    this.blockedByAgentDbId.clear();
    this.pendingByAgentDbId.clear();

    if (nextAgentDbId) {
      void this.isAgentMergeBlocked(nextAgentDbId).catch(() => {
        this.pendingByAgentDbId.delete(nextAgentDbId);
      });
    }
  }

  private async loadIsAgentMergeBlocked(agentDbId: string): Promise<boolean> {
    const query = [new QueryParam('execution.status', WhereFilterOperandKeys.in, this.blockedStatuses)];
    const requests = await firstValueFrom(this.agentMergeRequestsService.getList(query).pipe(take(1)));

    const isBlocked = (requests ?? []).some(
      request => request?.targetAgentDbId === agentDbId || request?.sourceAgentDbId === agentDbId,
    );

    this.blockedByAgentDbId.set(agentDbId, isBlocked);

    return isBlocked;
  }
}
